import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import { Box, CircularProgress, Dialog, Typography } from "@mui/material";
import React, { useEffect, useRef, useState } from "react";

import { BatchAllocationResult } from "../../../../services/roster-assignments-api";
import { planner, PrimaryButton, SectionLabel } from "../../planner-ui"; // eslint-disable-line sort-imports

/**
 * The stages the allocator actually goes through, in order. The engine runs as
 * one request, so these are NARRATED rather than measured: each lights up in
 * turn while the call is in flight, and the last one holds until the server
 * answers. The stage names mirror the real pipeline (skills gate, then the
 * availability / same-shift / not-already-assigned / seniority rules, then
 * parallel-group sequencing) so what the user reads is what the engine does.
 * Every NUMBER shown at the end is the server's own result, never a guess.
 */
const STAGES = [
	{ label: "Reading job orders", detail: "production work scheduled for this day" },
	{ label: "Reading labor standards", detail: "operations, phases and parallel groups" },
	{ label: "Matching with the skills matrix", detail: "only qualified workers are eligible" },
	{ label: "Checking associate availability", detail: "who is rostered, and who is already committed" },
	{ label: "Applying preferences", detail: "same shift first, seniority, no double-booking" },
	{ label: "Optimizing allocation", detail: "sequencing the plan and filling every phase" },
];

const STAGE_MS = 1150;

interface AllocationProgressDialogProps {
	open: boolean
	dayLabel: string
	orderCount: number
	/** Null while the request is in flight. */
	result: BatchAllocationResult | null
	error?: string | null
	onClose: () => void
}

const StatTile: React.FC<{ value: string | number; label: string; tone?: "default" | "accent" | "warn" }> = ({
	value,
	label,
	tone = "default",
}) => (
	<Box
		sx={{
			flex: 1,
			minWidth: 0,
			px: 1.5,
			py: 1.25,
			borderRadius: `${planner.radiusSm}px`,
			border: `1px solid ${tone === "warn" ? planner.accentBorder : planner.hairline}`,
			bgcolor: tone === "accent" ? planner.accentSoft : planner.surfaceSubtle,
		}}
	>
		<Typography
			sx={{
				...planner.type.pageTitle,
				fontFamily: planner.fontMono,
				fontVariantNumeric: "tabular-nums",
				color: tone === "accent" ? planner.accentDeep : planner.ink,
				lineHeight: 1.1,
			}}
		>
			{value}
		</Typography>
		<Typography sx={{ ...planner.type.meta, color: planner.inkMuted, mt: 0.25 }}>{label}</Typography>
	</Box>
);

function AllocationProgressDialog({
	open,
	dayLabel,
	orderCount,
	result,
	error,
	onClose,
}: AllocationProgressDialogProps) {
	const [elapsed, setElapsed] = useState(0);
	const startedAt = useRef<number>(0);
	const responded = Boolean(result) || Boolean(error);

	// Progress is derived from ELAPSED TIME, not from counting interval ticks.
	// An interval gets torn down and restarted whenever this effect's deps change
	// (the response landing, the parent re-rendering as assignments refetch),
	// which made the walk stall and then jump several stages at once. A start
	// timestamp is immune to that: re-renders cannot change how much time passed.
	const wasResponded = useRef(false);
	useEffect(() => {
		if (!open) {
			startedAt.current = 0;
			setElapsed(0);
			wasResponded.current = false;
			return;
		}
		// A second Allocate without closing the dialog is a NEW run: the caller
		// clears the result, so responded flips back to false. Without restarting
		// the clock the stale start time makes the walk look already finished and
		// the dialog snaps straight to the result.
		if (wasResponded.current && !responded) {
			startedAt.current = 0;
			setElapsed(0);
		}
		wasResponded.current = responded;

		if (!startedAt.current) {
			startedAt.current = performance.now();
		}
		const tick = setInterval(() => {
			setElapsed(performance.now() - startedAt.current);
		}, 100);
		return () => clearInterval(tick);
	}, [open, responded]);

	// The final step is only reached once the server has answered, so the dialog
	// never claims to be done before it is; a slow server just holds the last stage.
	const walked = Math.floor(elapsed / STAGE_MS);
	const activeStage = Math.min(walked, responded ? STAGES.length : STAGES.length - 1);
	const finished = responded && activeStage >= STAGES.length;

	const summary = result?.summary;
	const totalOrders = summary?.totalWorkOrders ?? orderCount;
	const fullyStaffed = summary?.fullyStaffedWorkOrders ?? 0;
	const partially = summary?.partiallyStaffedWorkOrders ?? 0;
	const workers = summary?.totalWorkersAssigned ?? 0;

	return (
		<Dialog
			open={open}
			onClose={finished ? onClose : undefined}
			maxWidth="sm"
			fullWidth
			PaperProps={{
				sx: {
					borderRadius: `${planner.radius}px`,
					border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
					bgcolor: planner.surface,
					backgroundImage: "none",
				},
			}}
		>
			<Box sx={{ p: 3 }}>
				{/* Header */}
				<Box sx={{ mb: 2.5 }}>
					<SectionLabel>{finished ? "Allocation complete" : "Allocating"}</SectionLabel>
					<Typography sx={{ ...planner.type.title, color: planner.ink, mt: 0.5 }}>
						{dayLabel}
					</Typography>
					<Typography sx={{ ...planner.type.meta, color: planner.inkMuted }}>
						{orderCount} {orderCount === 1 ? "job order" : "job orders"} · matching workers to every
						phase
					</Typography>
				</Box>

				{/* Stages */}
				<Box sx={{ display: "flex", flexDirection: "column", gap: 0.25, mb: finished ? 2.5 : 0 }}>
					{STAGES.map((stage, index) => {
						const isDone = index < activeStage;
						const isActive = index === activeStage && !finished;
						const isPending = index > activeStage;
						return (
							<Box
								key={stage.label}
								sx={{
									display: "flex",
									alignItems: "flex-start",
									gap: 1.25,
									py: 0.6,
									opacity: isPending ? 0.38 : 1,
									transition: "opacity 200ms ease",
								}}
							>
								{/* Status marker */}
								<Box
									sx={{
										width: 18,
										height: 18,
										mt: 0.15,
										flexShrink: 0,
										borderRadius: "50%",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										bgcolor: isDone ? planner.accent : "transparent",
										border: isDone ? "none" : `1.5px solid ${planner.hairlineStrong}`,
									}}
								>
									{isDone && <CheckIcon sx={{ fontSize: 12, color: planner.surface }} />}
									{isActive && <CircularProgress size={12} sx={{ color: planner.accent }} />}
								</Box>

								<Box sx={{ minWidth: 0 }}>
									<Typography
										sx={{
											...(isActive ? planner.type.bodyStrong : planner.type.body),
											color: isPending ? planner.inkMuted : planner.ink,
										}}
									>
										{stage.label}
									</Typography>
									<Typography sx={{ ...planner.type.meta, color: planner.inkMuted }}>
										{stage.detail}
									</Typography>
								</Box>
							</Box>
						);
					})}
				</Box>

				{/* Result */}
				{error && (
					<Box
						sx={{
							display: "flex",
							gap: 1,
							alignItems: "flex-start",
							p: 1.5,
							mt: 2,
							borderRadius: `${planner.radiusSm}px`,
							border: `1px solid ${planner.accentBorder}`,
							bgcolor: planner.accentSoft,
						}}
					>
						<ErrorOutlineIcon sx={{ fontSize: 18, color: planner.accentDeep, mt: 0.1 }} />
						<Typography sx={{ ...planner.type.body, color: planner.accentDeep }}>{error}</Typography>
					</Box>
				)}

				{result && !error && totalOrders === 0 && (
					<Typography sx={{ ...planner.type.body, color: planner.inkMuted }}>
						No production steps are scheduled on {dayLabel} at this plant, so there was nothing to staff.
						Upload or add the day&apos;s plan first, then run Allocate again.
					</Typography>
				)}

				{result && !error && totalOrders > 0 && (
					<>
						<Box sx={{ display: "flex", gap: 1 }}>
							<StatTile value={totalOrders} label="job orders" />
							<StatTile
								value={`${fullyStaffed}/${totalOrders}`}
								label="fully staffed"
								tone={fullyStaffed === totalOrders ? "accent" : "warn"}
							/>
							<StatTile value={workers} label="workers assigned" />
						</Box>
						<Typography sx={{ ...planner.type.meta, color: planner.inkMuted, mt: 1.5 }}>
							{fullyStaffed === totalOrders
								? "Every phase on every job order has the people it needs."
								: `${partially} partially staffed — some phases could not be filled from the workers available on this day.`}
						</Typography>
					</>
				)}

				{/* Actions */}
				{finished && (
					<Box sx={{ display: "flex", justifyContent: "flex-end", mt: 2.5 }}>
						<PrimaryButton onClick={onClose}>View roster</PrimaryButton>
					</Box>
				)}
			</Box>
		</Dialog>
	);
}

export default AllocationProgressDialog;
